import { useState, useCallback } from 'react';

// ==============================|| GEO LOCATION SERVICE ||============================== //

/**
 * Standalone function — can be called from anywhere (not tied to React state).
 * Returns a Promise that resolves with the "lat,lng" string,
 * or rejects if the user denies / browser doesn't support geolocation.
 */
let cachedCoordinates = null;
let cachedStatus = 'pending';

export const resetGeoLocationCache = () => {
  cachedCoordinates = null;
  cachedStatus = 'pending';
};

export const fetchGeoLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const msg = 'Geolocation is not supported by this browser.';
      console.error(msg);
      reject(new Error(msg));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const coordinates = `${lat},${lng}`;
        cachedCoordinates = coordinates;
        cachedStatus = 'granted';
        resolve(coordinates);
      },
      (error) => {
        let msg = '';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            msg = 'Location permission denied. Please enable location access in your browser settings to continue.';
            break;
          case error.POSITION_UNAVAILABLE:
            msg = 'Location information is unavailable. Please check your device settings.';
            break;
          case error.TIMEOUT:
            msg = 'Location request timed out. Please try again.';
            break;
          default:
            msg = 'An unknown error occurred while fetching location.';
            break;
        }
        console.error('GeoLocation error:', msg);
        reject(new Error(msg));
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 600000 // cache position for 5 minutes
      }
    );
  });
};

// ==============================|| GEO LOCATION HOOK (for React components) ||============================== //

const useGeoLocation = () => {
  const [latLong, setLatLong] = useState(cachedCoordinates);
  // 'pending' = waiting for user decision, 'granted' = allowed, 'denied' = blocked/unsupported
  const [locationStatus, setLocationStatus] = useState(cachedStatus);
  const [errorMessage, setErrorMessage] = useState('');

  /**
   * Calls the standalone fetchGeoLocation and updates React state accordingly.
   */
  const fetchLocation = useCallback(async () => {
    try {
      const coordinates = await fetchGeoLocation();
      cachedCoordinates = coordinates;
      cachedStatus = 'granted';
      setLatLong(coordinates);
      setLocationStatus('granted');
      setErrorMessage('');
      return coordinates;
    } catch (err) {
      cachedCoordinates = null;
      cachedStatus = 'denied';
      setLocationStatus('denied');
      setErrorMessage(err.message);
      setLatLong(null);
      throw err;
    }
  }, []);

  /**
   * Legacy-compatible wrapper (kept for backward-compat).
   */
  const setLocation = useCallback(() => {
    fetchLocation().catch(() => {
      // errors are already handled inside fetchLocation
    });
  }, [fetchLocation]);

  return {
    latLong,
    locationStatus, // 'pending' | 'granted' | 'denied'
    locationGeoFlag: locationStatus !== 'granted', // true when location NOT available (backward-compat)
    errorMessage,
    setLocation,
    fetchGeoLocation: fetchLocation // React-state-aware wrapper
  };
};

export default useGeoLocation;
